import { DateTime } from "luxon";

/** Weeks are ISO weeks: Monday → Sunday. */
export type WeekRange = {
  /** Monday of the first selected week. */
  start: DateTime;
  /** Monday of the last selected week. */
  end: DateTime;
};

export type WeekBounds = {
  /** Monday of the earliest week that overlaps the data window. */
  firstWeek: DateTime;
  /** Monday of the latest fully-completed week. */
  lastWeek: DateTime;
};

export type WeekPreset = {
  id: string;
  label: string;
  /** Number of trailing complete weeks, or "YTD" for weeks since January 1. */
  weeks: number | "YTD";
};

/** Week-aligned counterparts of the day-based DatePicker presets (L7D, L30D, …). */
export const WEEK_PRESETS: WeekPreset[] = [
  { id: "L1W", label: "Last week", weeks: 1 },
  { id: "L4W", label: "Last 4 weeks", weeks: 4 },
  { id: "L12W", label: "Last 12 weeks", weeks: 12 },
  { id: "L24W", label: "Last 24 weeks", weeks: 24 },
  { id: "YTDW", label: "Year to date", weeks: "YTD" },
];

export const isWeekPresetId = (id: string | null | undefined): boolean =>
  !!id && WEEK_PRESETS.some((preset) => preset.id === id);

export const toWeekStart = (date: Date | DateTime): DateTime => {
  const dt = date instanceof Date ? DateTime.fromJSDate(date) : date;
  return dt.startOf("week").startOf("day");
};

export const weekEndOf = (weekStart: DateTime): DateTime =>
  weekStart.plus({ days: 6 }).endOf("day");

/**
 * A week is selectable when it has finished (its Sunday is on or before
 * `maxDate`, defaulting to yesterday) and it overlaps the data window
 * (its Sunday is on or after `minDate`).
 */
export const getWeekBounds = ({
  minDate,
  maxDate,
  today = DateTime.now(),
}: {
  minDate?: Date | null;
  maxDate?: Date | null;
  today?: DateTime;
}): WeekBounds | null => {
  const yesterday = today.minus({ days: 1 }).endOf("day");
  const maxEnd =
    maxDate && DateTime.fromJSDate(maxDate) < yesterday
      ? DateTime.fromJSDate(maxDate).endOf("day")
      : yesterday;

  let lastWeek = toWeekStart(maxEnd);
  if (weekEndOf(lastWeek) > maxEnd) lastWeek = lastWeek.minus({ weeks: 1 });

  const firstWeek = minDate
    ? toWeekStart(DateTime.fromJSDate(minDate))
    : lastWeek.minus({ weeks: 103 });

  if (firstWeek > lastWeek) return null;
  return { firstWeek, lastWeek };
};

export const isWeekSelectable = (
  weekStart: DateTime,
  bounds: WeekBounds | null,
): boolean =>
  !!bounds && weekStart >= bounds.firstWeek && weekStart <= bounds.lastWeek;

const normalizeRange = (a: DateTime, b: DateTime): WeekRange =>
  a <= b ? { start: a, end: b } : { start: b, end: a };

export const countWeeks = (range: WeekRange): number =>
  Math.round(range.end.diff(range.start, "weeks").weeks) + 1;

/**
 * Range for a preset, ending on the last complete week and trimmed to the
 * data window. "YTD" starts at the first Monday on or after January 1 of the
 * last complete week's year.
 */
export const getPresetRange = (
  weeks: WeekPreset["weeks"],
  bounds: WeekBounds | null,
): WeekRange | null => {
  if (!bounds) return null;
  let desiredStart: DateTime;
  if (weeks === "YTD") {
    const jan1 = bounds.lastWeek.startOf("year");
    desiredStart = jan1.weekday === 1 ? jan1 : toWeekStart(jan1).plus({ weeks: 1 });
    if (desiredStart > bounds.lastWeek) desiredStart = bounds.lastWeek;
  } else {
    desiredStart = bounds.lastWeek.minus({ weeks: weeks - 1 });
  }
  const start = desiredStart < bounds.firstWeek ? bounds.firstWeek : desiredStart;
  return { start, end: bounds.lastWeek };
};

/** The preset whose range exactly matches `range`, if any. */
export const findMatchingPreset = (
  range: WeekRange,
  bounds: WeekBounds | null,
): WeekPreset | undefined =>
  WEEK_PRESETS.find((preset) => {
    const presetRange = getPresetRange(preset.weeks, bounds);
    return (
      !!presetRange &&
      presetRange.start.toMillis() === range.start.toMillis() &&
      presetRange.end.toMillis() === range.end.toMillis()
    );
  });

/** Monday-start rows covering every day of the month containing `month`. */
export const getMonthWeeks = (month: DateTime): DateTime[] => {
  const first = toWeekStart(month.startOf("month"));
  const last = toWeekStart(month.endOf("month"));
  const weeks: DateTime[] = [];
  for (let w = first; w <= last; w = w.plus({ weeks: 1 })) weeks.push(w);
  return weeks;
};

/** Snaps an arbitrary day range to the weeks it touches. */
export const rangeFromDates = (startDate: Date, endDate: Date): WeekRange =>
  normalizeRange(toWeekStart(startDate), toWeekStart(endDate));

/**
 * Converts a week range to API dates. The start is clamped to `minDate`, so a
 * partially-available first week only requests days that have data.
 */
export const toApiDates = (
  range: WeekRange,
  minDate?: Date | null,
): { startDate: string; endDate: string } => {
  const min = minDate ? DateTime.fromJSDate(minDate).startOf("day") : null;
  const start = min && range.start < min ? min : range.start;
  return {
    startDate: start.toFormat("yyyy-MM-dd"),
    endDate: weekEndOf(range.end).toFormat("yyyy-MM-dd"),
  };
};
export const formatDayRange = (start: DateTime, end: DateTime): string => {
  if (start.year !== end.year) {
    return `${start.toFormat("MMM d, yyyy")} – ${end.toFormat("MMM d, yyyy")}`;
  }
  if (start.month === end.month) {
    return `${start.toFormat("MMM d")} – ${end.toFormat("d, yyyy")}`;
  }
  return `${start.toFormat("MMM d")} – ${end.toFormat("MMM d, yyyy")}`;
};
